import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Paperclip, X } from 'lucide-react';
import { Button } from '../common/Button';
import { useHubStore } from '../../stores/hubStore';
import type { HubAttachment } from '../../types/hub';
import FullBlockingModal from '../common/FullBlockingModal';

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

interface AddMessageModalProps {
  jobId: string;
  quoteId?: string | null;
  recipientIds: string[];
  onClose: () => void;
  onSent: () => void;
}

export const AddMessageModal: React.FC<AddMessageModalProps> = ({
  jobId,
  quoteId,
  recipientIds,
  onClose,
  onSent,
}) => {
  const { t } = useTranslation('hub');
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState<HubAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { sendHubMessage, uploadHubAttachment } = useHubStore();

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;

    setError(null);
    setUploading(true);

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (file.size > MAX_FILE_SIZE) {
          setError(t('fileTooLarge'));
          continue;
        }
        const result = await uploadHubAttachment(jobId, file, quoteId);
        setAttachments(prev => [...prev, result]);
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeAttachment = (path: string) => {
    setAttachments(prev => prev.filter(a => a.path !== path));
  };

  const handleSubmit = async () => {
    if ((!message.trim() && attachments.length === 0) || recipientIds.length === 0) {
      setError(
        recipientIds.length === 0
          ? t('messageOrAttachmentRequired')
          : t('messageOrAttachmentRequired')
      );
      return;
    }

    setSending(true);
    setError(null);

    try {
      await sendHubMessage(jobId, recipientIds, message.trim(), attachments, quoteId);
      onSent();
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSending(false);
    }
  };

  return (
    <FullBlockingModal
      className="p-4"
      innerClassName="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl"
      onBackdropClick={onClose}
    >
      <div>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-bold text-secondary">{t('addMessage')}</h3>
          <button type="button" onClick={onClose} className="rounded p-1 hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        <textarea
          value={message}
          onChange={e => setMessage(e.target.value)}
          placeholder={t('messagePlaceholder')}
          className="mb-3 w-full rounded-lg border border-gray-300 p-3 text-sm"
          rows={4}
        />

        <div className="mb-4">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-2 rounded border border-gray-300 px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
          >
            <Paperclip className="h-4 w-4" />
            {uploading ? t('loading') : t('attachFile')}
          </button>
          {attachments.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {attachments.map(att => (
                <span
                  key={att.path}
                  className="flex items-center gap-1 rounded bg-gray-100 px-2 py-1 text-xs"
                >
                  {att.file_name}
                  <button
                    type="button"
                    onClick={() => removeAttachment(att.path)}
                    className="text-red-600 hover:underline"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {error && <p className="mb-4 text-sm text-red-600">{error}</p>}

        <div className="flex gap-2 justify-end">
          <Button variant="alternate" onClick={onClose}>
            {t('common:form_actions.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={sending || (uploading && attachments.length === 0)}
          >
            {sending ? t('loading') : t('submitMessage')}
          </Button>
        </div>
      </div>
    </FullBlockingModal>
  );
};
